/**
 *
 * PhotoUrl
 *
 */
import { error } from 'core/components/Notify';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { fireInput } from 'core/utils/constants';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import { getImage } from 'core/utils/constants';
import React, { memo, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import Avatar from "assets/icons/Avatar.svg";

interface Props {
  size?: string;
  relative?: string;
  selectedFile: File[] | null | undefined | any;
  URL: string;
  setSelectedFile: React.Dispatch<React.SetStateAction<File[] | undefined>> | any;
  setField?: any;
  uploadToS3?: boolean;
  customSetFile?: any;
  noBorder?: any;
  dest?: any;
  id?: any;
  validatePngOnly?: boolean;
  label?: string;
}

const destIds: any = [
  { dest: "STAFF", idName: "staff_id" },
  { dest: "PARENT", idName: "parent_id" },
  { dest: "STUDENT", idName: "student_id" },
  { dest: "CHATGROUP", idName: "" },
  { dest: "CHATTHREAD", idName: "thread_id" },
  { dest: "HOMEWORK", idName: "homework_id" },
  { dest: "BRANCHDOCS", idName: "" },
  { dest: "SUPPORTTICKET", idName: "ticket_id" },
  { dest: "STUDENTDOCS", idName: "" },
  { dest: "EXPENSE", idName: "" },
  { dest: "VISITOR", idName: "" },
  { dest: "CALLTEMPLATE", idName: "" },
  { dest: "WEBSITE", idName: "branch_id" },
]

export const PhotoUrl = memo((props: Props) => {
  const authData = useAuth();
  const [preview, setPreview] = useState<any>();
  const [randomValue, setRandomValue] = useState<any>(Math.random());
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  /* File Changes */
  useEffect(() => {
    if (!props.selectedFile || props.selectedFile.length === 0) {
      setPreview(undefined);
      return;
    }
    
    let objectUrl = "";
    
    // Handle File objects differently from string URLs
    if (Array.isArray(props.selectedFile) && props.selectedFile[0] instanceof File) {
      objectUrl = URL.createObjectURL(props.selectedFile[0]);
      setPreview(objectUrl);
    } else if (typeof props.selectedFile === "string") {
      setPreview(props.selectedFile);
    } else if (Array.isArray(props.selectedFile) && typeof props.selectedFile[0] === "string") {
      setPreview(props.selectedFile[0]);
    }
    
    // free memory when component is unmounted
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [props.selectedFile]);

  const handleFileChange = e => {
    if (!e.target.files || e.target.files.length === 0) return;

    /* Should not be greater than 300kb */
    const file = e.target.files[0];
    if (file.size >= 300000) {
      error('File Size Should be less than 300Kb!', true);
      return;
    }
    if (props.validatePngOnly && file.type !== 'image/png') {
      error('Only PNG files are allowed!', true);
      return;
    }

    if (props.customSetFile) props.customSetFile([file]); 
    else props.setSelectedFile([file]);
  };

  useEffect(() => {
    const uploadFile = async () => {
      if (!props.selectedFile || props.selectedFile.length !== 1 || isUploading) return;
      
      setIsUploading(true);
      
      try {
        if (props.uploadToS3) {
          const formData = new FormData();
          formData.append('file', props.selectedFile[0] as File);
          
          const s3Url: string = await apiFileUpload(formData, 'upload');
          const imageUrl = getImage(s3Url);
          
          if (!!props?.setSelectedFile) {
            // Type-safe way to handle the string URL
            if (typeof props.setSelectedFile === 'function') {
              // If it's expected to be a state setter, we need to wrap it
              props.setSelectedFile(() => imageUrl);
            } else {
              // If it's a simple setter function
              props.setSelectedFile(imageUrl);
            }
          }
          
          if (!!props.setField) {
            props.setField(imageUrl);
          }
          
          // Update preview with the new URL
          setPreview(imageUrl);
          
        } else if (!!props?.dest && !!props?.id) {
          const formData = new FormData();
          formData.append('files', props.selectedFile[0] as File);
          formData.append('dest', props?.dest);
          formData.append('branch_id', authData?.BRANCH_ID as any);
          formData.append('academic_year_id', authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID as any);
          formData.append('database', authData?.DATABASE as any);
          
          const destItem = destIds?.find(item => item?.dest === props?.dest);
          if (destItem?.idName) {
            formData.append(destItem.idName, props?.id);
          }
          
          const s3Response: any = await apiFileUpload(
            formData,
            'upload',
            '',
            '',
            true,
            false,
          );
          
          let obj = JSON.parse(s3Response);
          const uploadedUrl = Object.values(obj?.data)?.[0] as string;
          
          if (!!props?.setSelectedFile) {
            // Type-safe way to handle the string URL
            if (typeof props.setSelectedFile === 'function') {
              // If it's expected to be a state setter, we need to wrap it
              props.setSelectedFile(() => uploadedUrl);
            } else {
              // If it's a simple setter function
              props.setSelectedFile(uploadedUrl);
            }
          }
          
          if (!!props.setField) {
            props.setField(uploadedUrl);
          }
          
          // Update preview with the new URL and append randomValue to force refresh
          setPreview(uploadedUrl);
          setRandomValue(Math.random());
        }
      } catch (err) {
        console.error("File upload error:", err);
        error('File upload failed. Please try again.', true);
      } finally {
        setIsUploading(false);
      }
    };
    
    uploadFile();
  }, [props.selectedFile]);

  const handleError = event => {
    event.target.onerror = null;
    event.target.src = Avatar;
  };

  // Function to generate image URL with cache-busting
  const getImageUrl = (url) => {
    if (!url) return Avatar;
    return `${url}${url.includes('?') ? '&' : '?'}random=${randomValue}`;
  };

  return (
    <Div className={`w-fit mx-auto flex flex-col justify-center items-center ${!props?.noBorder ? "border border-dotted border-gray-400" : ""} py-2 px-2 rounded-lg gap-4`}>
      <input
        accept={props?.validatePngOnly ? `image/png` : `image/png, image/gif, image/jpeg`}
        type="file"
        ref={inputRef}
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />
      <div>
        {isUploading ? (
          <div className={`rounded-full shadow-lg flex justify-center items-center ${props.relative} ${props.size} bg-gray-200`}>
            <span className="text-sm">Uploading...</span>
          </div>
        ) : props.URL && (!props.selectedFile || (props.selectedFile && !props.selectedFile.length)) ? (
          <img
            onClick={() => fireInput(inputRef)}
            className={`rounded-full shadow-lg flex justify-center items-center cursor-pointer ${props.relative} object-cover ${props.size}`}
            src={getImageUrl(props.URL)}
            onError={handleError}
            alt="Portrait"
          />
        ) : !!props.selectedFile ? (
          <img
            onClick={() => fireInput(inputRef)}
            className={`rounded-full shadow-lg flex justify-center items-center cursor-pointer ${props.relative} object-cover ${props.size}`}
            src={getImageUrl(preview || props.URL)}
            onError={handleError}
            alt="Portrait"
          />
        ) : (
          <div
            onClick={() => fireInput(inputRef)}
            className={`rounded-full shadow-lg flex justify-center items-center cursor-pointer text-primary-main font-bold border-2 border-primary-50 ${props.relative} ${props.size}`}
          >
            Upload
          </div>
        )}
      </div>
      <p className='text-center text-neutral-60'>
        {!!props?.label ? props?.label : "Upload File should not be more than 300 kb"}
        <br />
        {props?.validatePngOnly ? 'only PNG file allowed.' : 'only JPEG and PNG file allowed.'}
      </p>
    </Div>
  );
});

const Div = styled.div``;